import { readonly, ref, shallowRef, watch, type Ref } from "vue";
import type { ContentDocument, ContentRecord } from "../types/content";

function isDocument(value: unknown, id: string): value is ContentDocument {
  if (!value || typeof value !== "object") return false;
  const document = value as Partial<ContentDocument>;
  return document.schema_version === "2.0" && document.id === id && Array.isArray(document.blocks);
}

export function useDocument(record: Ref<ContentRecord | undefined>) {
  const document = shallowRef<ContentDocument>();
  const loading = ref(false);
  const error = ref("");
  let controller: AbortController | undefined;

  watch(record, async (next) => {
    controller?.abort();
    document.value = undefined;
    error.value = "";
    if (!next) return;

    controller = new AbortController();
    loading.value = true;
    try {
      const response = await fetch(`${import.meta.env.BASE_URL}content/${next.source}/${next.id}.json`, { signal: controller.signal });
      if (!response.ok) throw new Error(`正文加载失败 (${response.status})`);
      const value: unknown = await response.json();
      if (!isDocument(value, next.id)) throw new Error("正文数据格式不正确");
      document.value = value;
    } catch (reason) {
      if (reason instanceof DOMException && reason.name === "AbortError") return;
      error.value = reason instanceof Error ? reason.message : "正文加载失败";
    } finally {
      loading.value = false;
    }
  }, { immediate: true });

  return { document, loading: readonly(loading), error: readonly(error) };
}
